/* Cart inline suggester — shared by the cart PAGE and the cart DRAWER (one shared
 * line-block builder, per phase-07-cart.md Key Insight #2). Ported from the Broadcast
 * store's assets/afroyla-drawer-suggester.js.
 *
 * ONE card at a time (owner rule 2026-08-22): the gold "MATCHES YOUR {TYPE}" card
 * renders under a single cart line per view — the first line (DOM order) that has not
 * yet converted a suggestion this session. After an ADD the card moves to the NEXT
 * un-served line on the refreshed render; a line only gets a second turn once every
 * other line has been served (the served list then resets and the cycle restarts).
 *
 * Candidates come from the single afroyla-drawer-suggester-data Liquid island (per-line
 * tier-1 from set_items size-paired big↔small, Yemi/Nayo neutral-wallet tier-2
 * fallback). This file adds cross-line dedupe by variant id — one piece is offered
 * under one line only, first line in DOM order wins — and drops anchors that end up
 * empty. In-cart products are excluded upstream in Liquid.
 *
 * Candidate prices: the island always ships baseCents/payCents; whether the payable
 * (struck-base) pair shows follows the LIVE applied state — the server-rendered flag
 * OR the afroyla:discount:applied/:removed client events, so a code claimed via AJAX
 * restyles the card without a reload.
 *
 * Adds carry _attach_source: drawer-suggest (admin-countable); pre-order candidates are
 * decorated by afroyla-preorder-cart.js from the registry blobs the data island renders.
 *
 * The island re-arrives with every cart refresh but a swapped-in `<script src>` never
 * re-executes — so this file loads ONCE (from afroyla-cart-lines.liquid) and re-initialises
 * via a MutationObserver whenever the island's markup morphs back in. */
(function () {
  'use strict';

  if (window.__aflDrawerSuggesterInit) return;
  window.__aflDrawerSuggesterInit = true;

  var SERVED_KEY = 'afroyla:suggest:served';

  /* Live coupon state: null = unknown (trust the island's server flag), else the
   * latest client event wins. */
  var clientApplied = null;

  function servedList() {
    try { return JSON.parse(window.sessionStorage.getItem(SERVED_KEY)) || []; } catch (e) { return []; }
  }
  function markServed(key) {
    var list = servedList();
    if (list.indexOf(key) === -1) list.push(key);
    try { window.sessionStorage.setItem(SERVED_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function clearServed() {
    try { window.sessionStorage.removeItem(SERVED_KEY); } catch (e) {}
  }

  function appliedNow(data) {
    if (clientApplied !== null) return clientApplied;
    if (data.applied) return true;
    // A rendered discount chip in the cart's own form is as good as the flag —
    // covers the AJAX-claimed-before-first-render case.
    return !!document.querySelector('[data-discount-tags] [data-remove-discount]');
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  /* ---- inline blocks ---- */

  function init(root) {
    if (root.__init) return;
    root.__init = true;

    var dataEl = root.querySelector('[data-suggest-data]');
    if (!dataEl) return;
    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    var anchors = (data.anchors || []).filter(function (a) { return a.cands && a.cands.length; });
    if (!anchors.length) return;

    // Line-DOM order decides both display order and dedupe priority.
    var order = [];
    document.querySelectorAll('.afl-cart-line[data-item]').forEach(function (line) {
      order.push(line.getAttribute('data-item'));
    });
    anchors.sort(function (a, b) { return order.indexOf(a.key) - order.indexOf(b.key); });

    var seen = {};
    anchors.forEach(function (a) {
      a.cands = a.cands.filter(function (c) {
        if (seen[c.vid]) return false;
        seen[c.vid] = true;
        return true;
      });
    });
    anchors = anchors.filter(function (a) { return a.cands.length; });
    if (!anchors.length) return;

    // Turn-taking: the ONE rendered card belongs to the first anchor (line order)
    // that has not converted a suggestion yet. When every current anchor has been
    // served, the cycle resets and the first line may suggest again.
    var served = servedList();
    var allServed = anchors.every(function (a) { return served.indexOf(a.key) !== -1; });
    if (allServed) {
      clearServed();
      served = [];
    }
    var current = null;
    for (var i = 0; i < anchors.length; i++) {
      if (served.indexOf(anchors[i].key) === -1) { current = anchors[i]; break; }
    }
    if (!current) current = anchors[0];

    var isApplied = appliedNow(data);

    document.querySelectorAll('.afl-sg-card').forEach(function (n) { n.remove(); });

    // The current (rotation-selected) line gets the full open card; every other
    // eligible line keeps a quiet collapsed text chip the shopper can expand
    // on demand (owner ask 2026-08-22) — the boxed look returns while open.
    anchors.forEach(function (anchor) {
      var line = document.querySelector('.afl-cart-line[data-item="' + anchor.key + '"]');
      if (!line) return;

      var isCurrent = anchor.key === current.key;
      var block = el('div', isCurrent ? 'afl-sg-card' : 'afl-sg-card afl-sg-card--quiet');
      var isOpen = isCurrent;

      var chip = el('button', 'afl-sg-card__chip');
      chip.type = 'button';
      chip.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      chip.appendChild(document.createTextNode(anchor.matchLabel + ' '));
      var caret = el('span', 'afl-sg-card__caret', isOpen ? '▾' : '▸');
      chip.appendChild(caret);
      block.appendChild(chip);

      var rows = el('div', 'afl-sg-card__rows');
      if (!isOpen) rows.hidden = true;

      anchor.cands.forEach(function (cand) {
        var row = el('div', 'afl-sg-card__row');

        var img = document.createElement('img');
        img.src = cand.img;
        img.alt = cand.title;
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        row.appendChild(img);

        var mid = el('div', 'afl-sg-card__mid');
        var name = el('span', 'afl-sg-card__name', cand.display || cand.title);
        name.title = cand.title;
        mid.appendChild(name);
        var lineTxt = cand.line;
        if (cand.pre) lineTxt += cand.eta ? ' · ships ' + cand.eta : ' · pre-order';
        mid.appendChild(el('span', 'afl-sg-card__line', lineTxt));
        row.appendChild(mid);

        var priceCol = el('div', 'afl-sg-card__prices');
        if (isApplied && cand.payCents < cand.baseCents) {
          priceCol.appendChild(el('span', 'afl-sg-card__pay', cand.pay));
          priceCol.appendChild(el('s', 'afl-sg-card__base', cand.base));
        } else {
          priceCol.appendChild(el('span', 'afl-sg-card__pay', cand.base));
        }
        row.appendChild(priceCol);

        var add = el('button', 'afl-sg-card__add', 'Add');
        add.type = 'button';
        add.addEventListener('click', function () {
          add.disabled = true;
          add.textContent = '…';
          fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: cand.vid, quantity: 1, properties: { _attach_source: 'drawer-suggest' } }] })
          })
            .then(function (res) {
              if (!res.ok) throw new Error('add failed');
              add.textContent = 'Added ✓';
              add.classList.add('is-added');
              markServed(anchor.key);
              document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
            })
            .catch(function () {
              add.disabled = false;
              add.textContent = 'Add';
            });
        });
        row.appendChild(add);
        rows.appendChild(row);
      });

      block.appendChild(rows);
      chip.addEventListener('click', function () {
        var nowHidden = !rows.hidden;
        rows.hidden = nowHidden;
        caret.textContent = nowHidden ? '▸' : '▾';
        chip.setAttribute('aria-expanded', nowHidden ? 'false' : 'true');
        if (!isCurrent) block.classList.toggle('afl-sg-card--quiet', nowHidden);
      });

      line.after(block);
    });
  }

  function adoptAll(root) {
    (root || document).querySelectorAll('[data-drawer-suggester]').forEach(init);
  }

  /* code review H1: a cart mutation morphs the island's JSON back in, but the JS-built
   * .afl-sg-card siblings sit OUTSIDE the island — Horizon's morph (assets/morph.js)
   * matches same-tag sibling elements positionally when neither carries an id/key, so an
   * unmatched .afl-sg-card <div> gets paired against a following .afl-cart-line <div> and
   * mutated into it (or dropped once the child counts reconcile), destroying the card. The
   * island itself always survives correctly (server-rendered Liquid, matched by the same
   * mechanism), so re-reading it and rebuilding every card from scratch on every mutation
   * heals the DOM regardless of what the morph did to the old cards. */
  function reinitAll() {
    document.querySelectorAll('[data-drawer-suggester]').forEach(function (root) {
      root.__init = false;
      init(root);
    });
  }

  adoptAll(document);

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      var needsReinit = false;
      for (var i = 0; i < records.length; i++) {
        var record = records[i];
        if (record.type === 'childList') {
          var added = record.addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-drawer-suggester]')) init(node);
            else if (node.querySelector && node.querySelector('[data-drawer-suggester]')) adoptAll(node);
          }
        } else if (record.type === 'characterData') {
          // The island's JSON script tag survives the morph in place (same tag, no
          // id/key -> matched node), so a refreshed cart patches its text content
          // rather than replacing the element -- no addedNodes, only this.
          var parent = record.target.parentNode;
          if (parent && parent.nodeType === 1 && parent.closest && parent.closest('[data-suggest-data]')) {
            needsReinit = true;
          }
        }
      }
      if (needsReinit) reinitAll();
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  }

  /* Horizon's own cart mutations (qty/remove) and the theme:cart:refresh reverse bridge
   * (assets/afroyla-theme-events.js, now global -- code review C1) both end in this event.
   * When cart-items-component already has the section HTML (native qty/remove flow), its
   * own morph runs synchronously ahead of this handler settling, so re-init here already
   * sees fresh DOM. When the bridge's promise resolves before Horizon's own async section
   * re-fetch lands (add paths routed through theme:cart:refresh, including the suggester's
   * own "Add" button below), this call can still see stale content -- the characterData
   * watch above is the fallback that catches the DOM up once that fetch morphs in. */
  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(reinitAll).catch(reinitAll);
    } else {
      reinitAll();
    }
  });

  /* Coupon claimed/removed via AJAX (strip or cart form) — restyle the card's
   * prices immediately from the client truth; the island's server flag catches
   * up on the next real render. */
  document.addEventListener('afroyla:discount:applied', function () {
    clientApplied = true;
    reinitAll();
  });
  document.addEventListener('afroyla:discount:removed', function () {
    clientApplied = false;
    reinitAll();
  });
})();
